import { Component, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { ActivityModal, CustomerModal } from "./components/crmModals.jsx";
import { ChangePasswordScreen, LoginScreen, Sidebar, themeOptions } from "./components/appShell.jsx";
import { ApiError, api, clearSession, emptyResources, loadResources, readSession, saveSession, userFriendlyErrorMessage } from "./lib/api.js";
import { formatDate } from "./lib/format.js";
import {
  ActivitiesView,
  AdminView,
  CustomersView,
  Dashboard,
  EngagementView,
  FieldTrackingView,
  IncentivesView,
  LifecycleView,
  OperationsView,
  ProductsOrdersView,
  ReportsView,
  TeamPerformanceView,
} from "./views/index.js";

const WORKSPACE_REFRESH_MS = 5000;
const THEME_STORAGE_KEY = "crmTheme";
const VIEW_STORAGE_KEY = "crmActiveView";
const DEFAULT_THEME = "sage";
const DEFAULT_VIEW = "dashboard";
const validThemeIds = new Set(themeOptions.map((theme) => theme.id));
const validViewIds = new Set([
  "dashboard",
  "teams",
  "customers",
  "activities",
  "field",
  "lifecycle",
  "products",
  "operations",
  "engagement",
  "incentives",
  "reports",
  "admin",
]);

function App() {
  const [workspaceContext] = useState(readWorkspaceContext);
  const sessionScope = workspaceContext.slug || "demo";
  const [session, setSession] = useState(() => readSession(sessionScope));
  const [workspace, setWorkspace] = useState(() => session?.workspace || defaultDemoWorkspace());
  const [theme, setTheme] = useState(readTheme);
  const [view, setViewState] = useState(readView);
  const [sidebarOpen, setSidebarOpen] = useState(defaultSidebarOpen);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [report, setReport] = useState(null);
  const [resources, setResources] = useState(emptyResources());
  const [modal, setModal] = useState(null);
  const [employeeEditTargetId, setEmployeeEditTargetId] = useState("");
  const [workflowDraft, setWorkflowDraft] = useState(null);
  const [quickFocusValue, setQuickFocusValue] = useState("");
  const [filters, setFilters] = useState({ search: "", stage: "", type: "", vertical: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionWarning, setSessionWarning] = useState("");
  const workspaceRefreshInFlight = useRef(false);
  const workspaceLoadSequence = useRef(0);
  const sessionTokenRef = useRef(session?.token || "");

  const user = session?.user;

  function setView(nextView) {
    const safeView = resolveView(nextView, user);
    setViewState(safeView);
    saveView(safeView);
  }

  useEffect(() => {
    refreshLoginShell();
  }, [workspaceContext.slug]);

  useEffect(() => {
    const showUnexpectedError = (event) => {
      if (shouldIgnoreGlobalBrowserError(event)) return;
      setError("Something unexpected happened on this screen. Your data is safe. Please try the action again.");
    };
    window.addEventListener("error", showUnexpectedError);
    window.addEventListener("unhandledrejection", showUnexpectedError);
    return () => {
      window.removeEventListener("error", showUnexpectedError);
      window.removeEventListener("unhandledrejection", showUnexpectedError);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Theme persistence is nice-to-have; the app remains usable without storage.
    }
  }, [theme]);

  useEffect(() => {
    if (!session?.token || session.user?.mustChangePassword) return;
    loadWorkspace();
    const refresh = setInterval(() => {
      loadWorkspace({ silent: true });
    }, WORKSPACE_REFRESH_MS);
    return () => clearInterval(refresh);
  }, [session?.token, session?.user?.mustChangePassword]);

  useEffect(() => {
    if (!session?.token || session.user?.mustChangePassword) return undefined;
    const refreshVisibleWorkspace = () => {
      if (document.visibilityState === "hidden") return;
      loadWorkspace({ silent: true });
    };
    window.addEventListener("focus", refreshVisibleWorkspace);
    document.addEventListener("visibilitychange", refreshVisibleWorkspace);
    return () => {
      window.removeEventListener("focus", refreshVisibleWorkspace);
      document.removeEventListener("visibilitychange", refreshVisibleWorkspace);
    };
  }, [session?.token, session?.user?.mustChangePassword]);

  useEffect(() => {
    sessionTokenRef.current = session?.token || "";
  }, [session?.token]);

  useEffect(() => {
    if (view === "admin" && user?.role !== "admin") {
      setViewState(DEFAULT_VIEW);
      saveView(DEFAULT_VIEW);
      setModal(null);
    }
  }, [view, user?.role]);

  async function loadWorkspace({ silent = false } = {}) {
    const requestToken = session?.token;
    if (!requestToken) return;
    if (silent && workspaceRefreshInFlight.current) return;
    const requestSequence = workspaceLoadSequence.current + 1;
    workspaceLoadSequence.current = requestSequence;
    workspaceRefreshInFlight.current = true;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const workspaceResults = await Promise.allSettled([
        api("/session", { token: requestToken }),
        api("/users", { token: requestToken }),
        api("/customers", { token: requestToken }),
        api("/activities", { token: requestToken }),
        api("/reports/summary", { token: requestToken }),
        loadResources(requestToken),
      ]);
      const [verifiedSession, userData, customerData, activityData, reportData, resourceData] = resolveWorkspaceResults(workspaceResults);
      if (sessionTokenRef.current !== requestToken || workspaceLoadSequence.current !== requestSequence) return;
      const accessWarning = syncVerifiedSession(verifiedSession);
      setUsers(userData);
      setCustomers(customerData);
      setActivities(activityData);
      if (reportData) setReport(reportData);
      setResources((current) => preserveLastGoodResources(current, resourceData));
      setError(
        [
          accessWarning,
          resourceData.loadWarning,
          reportData ? "" : "Some CRM analytics could not refresh. You can keep working with the data shown and try again in a moment. Affected areas: Dashboard analytics.",
        ]
          .filter(Boolean)
          .join(" "),
      );
    } catch (err) {
      if (workspaceLoadSequence.current !== requestSequence) return;
      handleRequestError(err);
    } finally {
      if (workspaceLoadSequence.current === requestSequence) {
        workspaceRefreshInFlight.current = false;
        if (!silent) setLoading(false);
      }
    }
  }

  async function login(credentials) {
    setError("");
    try {
      const credentialBody = typeof credentials === "string"
        ? { userId: credentials }
        : {
            username: credentials?.username,
            password: credentials?.password,
          };
      const nextSession = await api("/auth/login", {
        method: "POST",
        body: { ...credentialBody, workspaceSlug: workspaceContext.slug },
      });
      const sessionPersisted = saveSession(nextSession, sessionScope);
      setSession(nextSession);
      setWorkspace(nextSession.workspace || workspace);
      setView(DEFAULT_VIEW);
      setSessionWarning(
        sessionPersisted
          ? ""
          : "This browser could not save your session. You can keep working in this tab, but sign in again after refresh.",
      );
    } catch (err) {
      setError(userFriendlyErrorMessage(err));
    }
  }

  async function changePassword(payload) {
    if (!session?.token) return false;
    setError("");
    try {
      const updatedSession = await api("/auth/change-password", {
        method: "POST",
        token: session.token,
        body: payload,
      });
      const nextSession = {
        ...session,
        ...updatedSession,
        token: session.token,
        user: updatedSession.user || { ...session.user, mustChangePassword: false },
      };
      const sessionPersisted = saveSession(nextSession, sessionScope);
      setSession(nextSession);
      setWorkspace(nextSession.workspace || workspace);
      setSessionWarning(
        sessionPersisted
          ? "Password changed. Loading your workspace now."
          : "Password changed. This browser could not save your session, so sign in again after refresh.",
      );
      setView(DEFAULT_VIEW);
      return true;
    } catch (err) {
      setError(userFriendlyErrorMessage(err));
      return false;
    }
  }

  function clearWorkspace() {
    setSession(null);
    setUsers([]);
    setCustomers([]);
    setActivities([]);
    setReport(null);
    setResources(emptyResources());
    setModal(null);
    setSessionWarning("");
  }

  function logout() {
    const token = session?.token;
    clearSession(sessionScope);
    clearWorkspace();
    setView(DEFAULT_VIEW);
    refreshLoginShell();
    setError("");
    if (token) void api("/auth/logout", { method: "POST", token }).catch(() => {});
  }

  function expireSession() {
    clearSession(sessionScope);
    clearWorkspace();
    setView(DEFAULT_VIEW);
    refreshLoginShell();
    setError("Your session expired. Please sign in again.");
  }

  async function refreshLoginShell() {
    let loginWorkspace = null;
    if (workspaceContext.isTrial) {
      try {
        const trialWorkspace = await api(`/workspaces/${encodeURIComponent(workspaceContext.slug)}`);
        setWorkspace(trialWorkspace);
        loginWorkspace = trialWorkspace;
      } catch (err) {
        setUsers([]);
        setError(userFriendlyErrorMessage(err));
        return;
      }
    } else {
      loginWorkspace = defaultDemoWorkspace();
      setWorkspace(loginWorkspace);
    }
    await refreshLoginUsers(loginWorkspace);
  }

  async function refreshLoginUsers(loginWorkspace = workspace) {
    if ((loginWorkspace?.loginMode || "demo") !== "demo") {
      setUsers([]);
      return;
    }
    try {
      const query = workspaceContext.isTrial ? `?workspaceSlug=${encodeURIComponent(workspaceContext.slug)}` : "";
      const loginUsers = await api(`/users${query}`);
      if (!Array.isArray(loginUsers)) {
        setUsers([]);
        setError("The CRM user list could not refresh. Please try again.");
        return;
      }
      setUsers(loginUsers);
    } catch (err) {
      const secureLoginMode = inferLoginModeFromDirectoryError(err);
      if (secureLoginMode) {
        setUsers([]);
        setWorkspace({ ...loginWorkspace, loginMode: secureLoginMode });
      }
      setError(userFriendlyErrorMessage(err));
    }
  }

  function handleRequestError(err) {
    if (Number(err?.status || 0) === 401) {
      expireSession();
      return;
    }
    if (Number(err?.status || 0) === 403 && err?.data?.code === "PASSWORD_CHANGE_REQUIRED") {
      const nextSession = {
        ...session,
        user: { ...session?.user, mustChangePassword: true },
      };
      setSession(nextSession);
      saveSession(nextSession, sessionScope);
      setError("");
      return;
    }
    setError(userFriendlyErrorMessage(err));
  }

  function syncVerifiedSession(verifiedSession) {
    const verifiedUser = verifiedSession?.user;
    if (!verifiedUser) return "";
    const hasDifferentProfile =
      verifiedUser.id !== session.user?.id ||
      verifiedUser.role !== session.user?.role ||
      verifiedUser.teamId !== session.user?.teamId ||
      verifiedUser.name !== session.user?.name ||
      Boolean(verifiedUser.mustChangePassword) !== Boolean(session.user?.mustChangePassword) ||
      verifiedUser.workspaceId !== session.user?.workspaceId ||
      verifiedSession.workspace?.id !== session.workspace?.id ||
      verifiedSession.businessDate !== session.businessDate ||
      verifiedSession.businessTimeZone !== session.businessTimeZone;
    if (!hasDifferentProfile) return "";
    const nextSession = {
      ...session,
      ...verifiedSession,
      token: session.token,
      user: verifiedUser,
    };
    const sessionPersisted = saveSession(nextSession, sessionScope);
    setSession(nextSession);
    setWorkspace(nextSession.workspace || workspace);
    setSessionWarning(
      sessionPersisted
      ? ""
      : "This browser could not save your latest session update. You can keep working in this tab.",
    );
    if (session.user?.role === "admin" && verifiedUser.role !== "admin") {
      setView(DEFAULT_VIEW);
      setModal(null);
      return "Your admin access changed. Showing your scoped dashboard instead.";
    }
    return "";
  }

  async function saveCustomer(payload, customerId, nextAction = "") {
    try {
      setError("");
      const returnTo = modal?.returnTo;
      const saved = await api(customerId ? `/customers/${customerId}` : "/customers", {
        method: customerId ? "PUT" : "POST",
        token: session.token,
        body: payload,
      });
      if (customerId) {
        setCustomers((items) => items.map((item) => (item.id === saved.id ? saved : item)));
      } else {
        setCustomers((items) => [saved, ...items]);
      }
      setQuickFocusValue(`customer:${saved.id}`);
      if (nextAction === "call" || nextAction === "visit") {
        setModal({
          type: "activity",
          customer: saved,
          activityType: nextAction,
          backTo: { type: "customer", customer: saved },
          backLabel: "Back to prospect form",
        });
      } else if (nextAction === "message") {
        setModal(null);
        setWorkflowDraft({ id: createWorkflowDraftId(), view: "engagement", type: "message", customerId: saved.id, ...quickMessageDraft(saved) });
        setView("engagement");
      } else if (nextAction === "order") {
        setModal(null);
        setWorkflowDraft({ id: createWorkflowDraftId(), view: "products", type: "order", customerId: saved.id, status: "Draft" });
        setView("products");
      } else if (returnTo?.type === "activity" && !customerId) {
        const draft = { ...(returnTo.draft || {}), customerId: saved.id };
        setModal({ type: "activity", customer: saved, activityType: draft.type, draft });
      } else {
        setModal(null);
      }
      loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function saveActivity(payload) {
    try {
      setError("");
      const saved = await api("/activities", {
        method: "POST",
        token: session.token,
        body: payload,
      });
      setActivities((items) => [saved, ...items]);
      setModal(null);
      loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function createResource(path, payload, options = {}) {
    try {
      setError("");
      const saved = await api(path, { method: "POST", token: session.token, body: payload });
      await loadWorkspace();
      if (saved?.welcomeEmail) {
        const status = saved.welcomeEmail.status;
        if (status !== "accepted") setError(status === "disabled"
          ? "Account created. Welcome email is disabled; configure SMTP to enable it."
          : "Account created, but the welcome email was not accepted for delivery. Contact your email administrator. Do not create the account again.");
      }
      return saved ?? true;
    } catch (err) {
      handleRequestError(err);
      if (options.throwOnError) throw err;
      return false;
    }
  }

  async function updateResource(path, payload = {}, options = {}) {
    try {
      setError("");
      const saved = await api(path, { method: "PUT", token: session.token, body: payload });
      await loadWorkspace();
      return saved ?? true;
    } catch (err) {
      handleRequestError(err);
      if (options.throwOnError) throw err;
      return false;
    }
  }

  async function createDropdownOption(category, label) {
    try {
      setError("");
      const saved = await api("/dropdown-options", {
        method: "POST",
        token: session.token,
        body: { category, label, value: label },
      });
      await loadWorkspace();
      return saved;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function hideCustomer(customer) {
    if (!customer) return;
    try {
      setError("");
      await api(`/customers/${customer.id}/hide`, { method: "POST", token: session.token, body: {} });
      setCustomers((items) => items.filter((item) => item.id !== customer.id));
      await loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function requestCustomerDeletion(customer) {
    if (!customer) return;
    try {
      setError("");
      await api(`/customers/${customer.id}/deletion-requests`, {
        method: "POST",
        token: session.token,
        body: { reason: "Permanent deletion requested from prospect list." },
      });
      await loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function restoreCustomer(customer) {
    if (!customer) return;
    try {
      setError("");
      const restored = await api(`/customers/${customer.id}/restore`, { method: "POST", token: session.token, body: {} });
      setCustomers((items) => [restored, ...items.filter((item) => item.id !== restored.id)]);
      await loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  async function approveExpense(expenseId) {
    try {
      setError("");
      await api(`/expenses/${expenseId}/approve`, { method: "PUT", token: session.token, body: {} });
      await loadWorkspace();
      return true;
    } catch (err) {
      handleRequestError(err);
      return false;
    }
  }

  function goBackModal() {
    setModal((current) => current?.backTo || null);
  }

  function activityDraftModal(draft = {}) {
    return {
      type: "activity",
      customer: customers.find((item) => item.id === draft.customerId),
      activityType: draft.type,
      draft,
    };
  }

  function openCustomerFromActivity(customerOrId, draft = {}) {
    const customer = resolveCustomerReference(customerOrId);
    setModal({
      type: "customer",
      customer,
      backTo: activityDraftModal(draft),
      backLabel: "Back to activity form",
    });
  }

  function addCustomerFromActivity(draft = {}) {
    setModal({
      type: "customer",
      returnTo: { type: "activity", draft },
      backTo: activityDraftModal(draft),
      backLabel: "Back to activity form",
    });
  }

  function openCustomerEditor(customerOrId = null) {
    if (customerOrId?.preventDefault) {
      setModal({ type: "customer" });
      return;
    }
    if (!customerOrId) {
      setModal({ type: "customer" });
      return;
    }
    const customer = typeof customerOrId === "string"
      ? customers.find((item) => item.id === customerOrId)
      : customerOrId;
    setModal({ type: "customer", customer });
  }

  function openActivityEditor(customer = null, activityType = "") {
    const nextModal = { type: "activity" };
    if (customer) nextModal.customer = customer;
    if (activityType) nextModal.activityType = activityType;
    setModal(nextModal);
  }

  function openEmployeeEditor(employeeId) {
    if (user?.role !== "admin" || !employeeId) return;
    setModal(null);
    setEmployeeEditTargetId(employeeId);
    setView("admin");
  }

  function openEmployeeOnboarding() {
    if (user?.role !== "admin") return;
    setModal(null);
    setEmployeeEditTargetId("");
    setView("admin");
  }

  function startCustomerOrder(customerOrId, draft = {}) {
    const customer = resolveCustomerReference(customerOrId);
    if (!customer?.id) return;
    setWorkflowDraft({ id: createWorkflowDraftId(), view: "products", type: "order", customerId: customer.id, ...draft });
    setView("products");
  }

  function startCustomerMessage(customerOrId, draft = {}) {
    const customer = resolveCustomerReference(customerOrId);
    if (!customer?.id) return;
    setWorkflowDraft({ id: createWorkflowDraftId(), view: "engagement", type: "message", customerId: customer.id, ...draft });
    setView("engagement");
  }

  function startRepTarget(rep, draft = {}) {
    if (!rep?.userId || !["manager", "admin"].includes(user?.role)) return;
    setWorkflowDraft({ id: createWorkflowDraftId(), view: "operations", type: "target", userId: rep.userId, ...draft });
    setView("operations");
  }

  function startRepExpense(rep, draft = {}) {
    if (!rep?.userId) return;
    setWorkflowDraft({ id: createWorkflowDraftId(), view: "operations", type: "expense", userId: rep.userId, ...draft });
    setView("operations");
  }

  function resolveCustomerReference(customerOrId) {
    return typeof customerOrId === "string" ? customers.find((item) => item.id === customerOrId) : customerOrId;
  }

  function consumeWorkflowDraft(id) {
    setWorkflowDraft((current) => (current?.id === id ? null : current));
  }

  const filteredCustomers = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return customers.filter((customer) => {
      const verticals = getBusinessVerticals(customer);
      const matchesSearch =
        !search ||
        [customer.name, customer.phone, customer.productInterest, customer.address, customer.industryType, customer.leadSource, verticals.join(" ")].some((value) =>
          String(value || "").toLowerCase().includes(search),
        );
      const matchesStage = !filters.stage || customer.status === filters.stage;
      const matchesType = !filters.type || customer.type === filters.type;
      const matchesVertical = !filters.vertical || verticals.some((vertical) => vertical.toLowerCase() === filters.vertical.toLowerCase());
      return matchesSearch && matchesStage && matchesType && matchesVertical;
    });
  }, [customers, filters]);
  const customerVerticalOptions = useMemo(
    () => [...new Set(customers.flatMap(getBusinessVerticals))].sort((a, b) => a.localeCompare(b)),
    [customers],
  );
  const quickCustomer = useMemo(
    () => selectQuickCustomer(customers, users, session?.businessDate),
    [customers, users, session?.businessDate],
  );
  const quickTargetRep = useMemo(
    () => selectQuickTargetRep(report?.targetPerformance ?? []),
    [report?.targetPerformance],
  );
  const quickCustomerOptions = useMemo(
    () => getQuickCustomerOptions(customers, users),
    [customers, users],
  );
  const quickRepOptions = useMemo(
    () => getQuickSalesRepOptions(users),
    [users],
  );
  const quickActionCustomer = useMemo(
    () => resolveQuickCustomerFocus(quickFocusValue, quickCustomerOptions, quickCustomer),
    [quickFocusValue, quickCustomerOptions, quickCustomer],
  );
  const quickActionRep = useMemo(
    () => resolveQuickRepFocus(quickFocusValue, quickRepOptions, report?.targetPerformance ?? [], quickTargetRep),
    [quickFocusValue, quickRepOptions, report?.targetPerformance, quickTargetRep],
  );

  if (!session?.token) {
    return <LoginScreen users={users} onLogin={login} error={error} workspace={workspace} isTrial={workspaceContext.isTrial} />;
  }

  if (session.user?.mustChangePassword) {
    return (
      <ChangePasswordScreen
        user={user}
        workspace={workspace}
        error={error}
        sessionWarning={sessionWarning}
        onChangePassword={changePassword}
        onLogout={logout}
      />
    );
  }

  const adminEmployeeEditor = user.role === "admin" ? openEmployeeEditor : undefined;
  const adminEmployeeOnboarding = user.role === "admin" ? openEmployeeOnboarding : undefined;

  return (
    <div className={`app-shell ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
      <Sidebar
        user={user}
        view={view}
        setView={setView}
        onLogout={logout}
        theme={theme}
        setTheme={setTheme}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <button className="sidebar-scrim" type="button" aria-label="Close navigation menu overlay" onClick={() => setSidebarOpen(false)} />
      <main className="main">
        <div className="workspace-toolbar">
          <button
            className={`drawer-toggle ${sidebarOpen ? "active" : ""}`}
            type="button"
            aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        {sessionWarning ? <div className="notice">{sessionWarning}</div> : null}
        {error ? <div className="notice">{error}</div> : null}
        {loading ? <div className="notice">Loading workspace data...</div> : null}
        <WorkspaceQuickActions
          user={user}
          quickCustomer={quickActionCustomer}
          quickTargetRep={quickActionRep}
          quickCustomerOptions={quickCustomerOptions}
          quickRepOptions={quickRepOptions}
          quickFocusValue={quickFocusValue}
          onQuickFocusChange={setQuickFocusValue}
          hasProducts={resources.products.length > 0}
          onNewProspect={() => openCustomerEditor(null)}
          onLogCall={() => openActivityEditor(quickActionCustomer, "call")}
          onLogVisit={() => openActivityEditor(quickActionCustomer, "visit")}
          onMessageDue={() => quickActionCustomer ? startCustomerMessage(quickActionCustomer, quickMessageDraft(quickActionCustomer)) : setView("engagement")}
          onDraftOrder={() => quickActionCustomer ? startCustomerOrder(quickActionCustomer, { status: "Draft" }) : setView("products")}
          onQuickTarget={() => quickActionRep ? startRepTarget(quickActionRep, quickTargetDraft(quickActionRep)) : setView("operations")}
          onAddEmployee={adminEmployeeOnboarding}
        />
        <ScreenErrorBoundary
          resetKey={`${session.token}:${view}`}
          onRetry={() => loadWorkspace()}
          onGoDashboard={() => setView(DEFAULT_VIEW)}
        >
          {view === "dashboard" ? (
            <Dashboard
              user={user}
              businessDate={session.businessDate}
              report={report}
              customers={customers}
              activities={activities}
              users={users}
              activityCustomFields={resources.activityCustomFields}
              openCustomer={openCustomerEditor}
              openActivity={() => openActivityEditor()}
              openCall={(customer) => openActivityEditor(customer, "call")}
              openVisit={(customer) => openActivityEditor(customer, "visit")}
              openMessage={startCustomerMessage}
              openEmployee={adminEmployeeEditor}
              openTarget={startRepTarget}
              openExpense={startRepExpense}
            />
          ) : null}
          {view === "teams" ? (
            <TeamPerformanceView
              report={report}
              openEmployee={adminEmployeeEditor}
              openTarget={startRepTarget}
              openExpense={startRepExpense}
            />
          ) : null}
          {view === "customers" ? (
            <CustomersView
              user={user}
              users={users}
              customers={filteredCustomers}
              verticalOptions={customerVerticalOptions}
              filters={filters}
              setFilters={setFilters}
              openCustomer={openCustomerEditor}
              openCall={(customer) => openActivityEditor(customer, "call")}
              openVisit={(customer) => openActivityEditor(customer, "visit")}
              openOrder={startCustomerOrder}
              openMessage={startCustomerMessage}
              hideCustomer={hideCustomer}
              hiddenCustomers={resources.hiddenCustomers}
              restoreCustomer={restoreCustomer}
              requestCustomerDeletion={requestCustomerDeletion}
              openEmployee={adminEmployeeEditor}
            />
          ) : null}
          {view === "activities" ? (
            <ActivitiesView
              activities={activities}
              customers={customers}
              users={users}
              activityCustomFields={resources.activityCustomFields}
              openActivity={() => openActivityEditor()}
              openCall={(customer) => openActivityEditor(customer, "call")}
              openVisit={(customer) => openActivityEditor(customer, "visit")}
              openMessage={startCustomerMessage}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
            />
          ) : null}
          {view === "reports" ? (
            <ReportsView
              report={{ ...report, fullReport: resources.fullReport }}
              activities={activities}
              customers={customers}
              users={users}
              activityCustomFields={resources.activityCustomFields}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
              openCall={(customer) => openActivityEditor(customer, "call")}
              openVisit={(customer) => openActivityEditor(customer, "visit")}
              openMessage={startCustomerMessage}
              openTarget={startRepTarget}
              openExpense={startRepExpense}
            />
          ) : null}
          {view === "products" ? (
            <ProductsOrdersView
              resources={resources}
              customers={customers}
              users={users}
              user={user}
              onCreate={createResource}
              onUpdate={updateResource}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
              openMessage={startCustomerMessage}
              workflowDraft={workflowDraft?.view === "products" ? workflowDraft : null}
              onWorkflowDraftHandled={consumeWorkflowDraft}
            />
          ) : null}
          {view === "operations" ? (
            <OperationsView
              businessDate={session.businessDate}
              resources={resources}
              customers={customers}
              users={users}
              user={user}
              onCreate={createResource}
              onApproveExpense={approveExpense}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
              openMessage={startCustomerMessage}
              onAddEmployee={adminEmployeeOnboarding}
              workflowDraft={workflowDraft?.view === "operations" ? workflowDraft : null}
              onWorkflowDraftHandled={consumeWorkflowDraft}
            />
          ) : null}
          {view === "field" ? (
            <FieldTrackingView
              businessDate={session.businessDate}
              resources={resources}
              customers={customers}
              users={users}
              onCreate={createResource}
              onCloseVisit={(visitId) => updateResource(`/activities/${visitId}/checkout`, {})}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
            />
          ) : null}
          {view === "lifecycle" ? (
            <LifecycleView
              resources={resources}
              customers={customers}
              openCustomer={openCustomerEditor}
              openCall={(customer) => openActivityEditor(customer, "call")}
              openVisit={(customer) => openActivityEditor(customer, "visit")}
              openMessage={startCustomerMessage}
              openOrder={startCustomerOrder}
            />
          ) : null}
          {view === "engagement" ? (
            <EngagementView
              resources={resources}
              customers={customers}
              users={users}
              onCreate={createResource}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
              workflowDraft={workflowDraft?.view === "engagement" ? workflowDraft : null}
              onWorkflowDraftHandled={consumeWorkflowDraft}
            />
          ) : null}
          {view === "incentives" ? (
            <IncentivesView
              resources={resources}
              users={users}
              user={user}
              onCreate={createResource}
              onUpdate={updateResource}
              openEmployee={adminEmployeeEditor}
              onAddEmployee={adminEmployeeOnboarding}
            />
          ) : null}
          {view === "admin" && user.role === "admin" ? (
            <AdminView
              resources={resources}
              users={users}
              report={report}
              user={user}
              focusEmployeeId={employeeEditTargetId}
              onEmployeeFocusHandled={() => setEmployeeEditTargetId("")}
              customers={customers}
              openCustomer={openCustomerEditor}
              openEmployee={adminEmployeeEditor}
              onCreate={createResource}
              onUpdate={updateResource}
            />
          ) : null}
        </ScreenErrorBoundary>
      </main>
      {modal?.type === "customer" ? (
        <CustomerModal
          user={user}
          users={users}
          customers={customers}
          customer={modal.customer}
          dropdownOptions={resources.dropdownOptions}
          onBack={modal.backTo ? goBackModal : undefined}
          backLabel={modal.backLabel}
          onClose={() => setModal(null)}
          onSave={saveCustomer}
          canDraftOrder={resources.products.length > 0}
          onAddEmployee={adminEmployeeOnboarding}
          onOpenEmployee={adminEmployeeEditor}
          onAddDropdownOption={user.role === "admin" ? createDropdownOption : undefined}
        />
      ) : null}
      {modal?.type === "activity" ? (
        <ActivityModal
          customers={customers}
          users={users}
          customer={modal.customer}
          activityType={modal.activityType}
          draft={modal.draft}
          businessDate={session.businessDate}
          activityCustomFields={resources.activityCustomFields}
          dropdownOptions={resources.dropdownOptions}
          onBack={modal.backTo ? goBackModal : undefined}
          backLabel={modal.backLabel}
          onClose={() => setModal(null)}
          onSave={saveActivity}
          onAddCustomer={addCustomerFromActivity}
          onOpenCustomer={openCustomerFromActivity}
          onAddDropdownOption={user.role === "admin" ? createDropdownOption : undefined}
        />
      ) : null}
    </div>
  );
}

class AppErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error(error);
  }

  render() {
    if (this.state.failed) {
      return (
        <section className="fallback-shell">
          <div className="fallback-card">
            <div className="brand-mark">FS</div>
            <h1>We could not load this screen.</h1>
            <p>Your data is safe. Refresh the page and continue; if this keeps happening, contact support.</p>
            <button className="primary-btn" type="button" onClick={() => window.location.reload()}>
              Refresh
            </button>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

function WorkspaceQuickActions({
  user,
  quickCustomer,
  quickTargetRep,
  quickCustomerOptions,
  quickRepOptions,
  quickFocusValue,
  onQuickFocusChange,
  hasProducts,
  onNewProspect,
  onLogCall,
  onLogVisit,
  onMessageDue,
  onDraftOrder,
  onQuickTarget,
  onAddEmployee,
}) {
  const canWorkCustomer = Boolean(quickCustomer);
  const canUseTarget = Boolean(quickTargetRep && ["manager", "admin"].includes(user?.role));
  const isEmployeeFocused = quickFocusValue.startsWith("employee:");
  const isCustomerFocused = quickFocusValue.startsWith("customer:");
  const focusLabel = isEmployeeFocused ? "Focused employee" : isCustomerFocused ? "Focused customer" : "Quick Actions";
  const [focusMenuOpen, setFocusMenuOpen] = useState(false);
  const focusName = isEmployeeFocused
    ? quickTargetRep?.name || "Choose a sales rep"
    : quickCustomer
      ? isCustomerFocused
        ? quickCustomer.name
        : `Next customer: ${quickCustomer.name}`
      : "Start common work from any screen";
  const pickerValueLabel = isEmployeeFocused
    ? quickTargetRep?.name || "Choose a sales rep"
    : isCustomerFocused
      ? quickCustomer?.name || "Choose a customer"
      : "Auto: next due customer";
  const customerActionTitle = isEmployeeFocused ? "Choose a customer or Auto to use customer actions" : "Add or assign a prospect first";
  function chooseFocus(value) {
    onQuickFocusChange(value);
    setFocusMenuOpen(false);
  }

  return (
    <section className="quick-action-strip" aria-label="Workspace quick actions">
      <div className="quick-action-summary">
        <span>{focusLabel}</span>
        <strong>{focusName}</strong>
      </div>
      <div className="quick-action-picker">
        <span>Work on</span>
        <button
          className="quick-action-select"
          type="button"
          aria-label={`Work on ${pickerValueLabel}`}
          aria-haspopup="listbox"
          aria-expanded={focusMenuOpen}
          onClick={() => setFocusMenuOpen((open) => !open)}
        >
          <span>{pickerValueLabel}</span>
          <span aria-hidden="true">v</span>
        </button>
        {focusMenuOpen ? (
          <div className="quick-action-menu" role="listbox" aria-label="Quick focus choices">
            <button type="button" role="option" aria-selected={!quickFocusValue} onClick={() => chooseFocus("")}>
              Auto: next due customer
            </button>
            {quickCustomerOptions.length ? <strong>Customers</strong> : null}
            {quickCustomerOptions.map((customer) => (
              <button
                key={customer.id}
                type="button"
                role="option"
                aria-selected={quickFocusValue === `customer:${customer.id}`}
                onClick={() => chooseFocus(`customer:${customer.id}`)}
              >
                {customer.name}
              </button>
            ))}
            {["manager", "admin"].includes(user?.role) && quickRepOptions.length ? <strong>Sales reps</strong> : null}
            {["manager", "admin"].includes(user?.role) ? quickRepOptions.map((rep) => (
              <button
                key={rep.id}
                type="button"
                role="option"
                aria-selected={quickFocusValue === `employee:${rep.id}`}
                onClick={() => chooseFocus(`employee:${rep.id}`)}
              >
                {rep.name}
              </button>
            )) : null}
          </div>
        ) : null}
      </div>
      <div className="quick-action-buttons">
        <button className="quick-action-btn primary" type="button" onClick={onNewProspect}>
          New Prospect
        </button>
        <button
          className="quick-action-btn"
          type="button"
          disabled={!canWorkCustomer}
          title={canWorkCustomer ? `Log a call for ${quickCustomer.name}` : customerActionTitle}
          onClick={onLogCall}
        >
          Log Call
        </button>
        <button
          className="quick-action-btn"
          type="button"
          disabled={!canWorkCustomer}
          title={canWorkCustomer ? `Log a visit for ${quickCustomer.name}` : customerActionTitle}
          onClick={onLogVisit}
        >
          Log Visit
        </button>
        <button
          className="quick-action-btn"
          type="button"
          disabled={!canWorkCustomer}
          title={canWorkCustomer ? `Send a follow-up to ${quickCustomer.name}` : customerActionTitle}
          onClick={onMessageDue}
        >
          Message Due
        </button>
        <button
          className="quick-action-btn"
          type="button"
          disabled={!canWorkCustomer || !hasProducts}
          title={!hasProducts ? "Add a product before drafting orders" : canWorkCustomer ? `Draft an order for ${quickCustomer.name}` : customerActionTitle}
          onClick={onDraftOrder}
        >
          Draft Order
        </button>
        {["manager", "admin"].includes(user?.role) ? (
          <button
            className="quick-action-btn"
            type="button"
            disabled={!canUseTarget}
            title={canUseTarget ? `Review target for ${quickTargetRep.name}` : "No active target gaps need attention"}
            onClick={onQuickTarget}
          >
            Quick Target
          </button>
        ) : null}
        {user?.role === "admin" ? (
          <button className="quick-action-btn" type="button" onClick={onAddEmployee}>
            Add Employee
          </button>
        ) : null}
      </div>
    </section>
  );
}

class ScreenErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  componentDidCatch(error) {
    console.error(error);
  }

  retry = () => {
    this.setState({ failed: false });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.failed) {
      return (
        <section className="screen-fallback panel">
          <h2>This section could not load.</h2>
          <p className="subtle">The rest of the CRM is still available. Try refreshing this section, or move to the dashboard and continue working.</p>
          <div className="row-actions">
            <button className="primary-btn" type="button" onClick={this.retry}>
              Try Again
            </button>
            <button className="ghost-btn" type="button" onClick={this.props.onGoDashboard}>
              Go to Dashboard
            </button>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("app")).render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>,
);

function shouldIgnoreGlobalBrowserError(event) {
  const target = event?.target;
  if (target && target !== window) return true;
  const filename = String(event?.filename || target?.src || target?.href || "");
  if (/\.(avif|gif|ico|jpe?g|png|svg|webp)(\?|#|$)/i.test(filename)) return true;
  if (/^(chrome|moz|safari)-extension:\/\//i.test(filename)) return true;
  if (event?.reason?.name === "AbortError") return true;
  return false;
}

function defaultSidebarOpen() {
  if (typeof window === "undefined") return true;
  return !window.matchMedia("(max-width: 620px)").matches;
}

function readWorkspaceContext() {
  if (typeof window === "undefined") return { isTrial: false, slug: "demo" };
  const path = window.location.pathname.replace(/^\/crmai\d+(?=\/trial\/)/i, "");
  const match = path.match(/^\/trial\/([^/]+)/i);
  const slug = match ? decodeURIComponent(match[1] || "").trim().toLowerCase() : "demo";
  return { isTrial: Boolean(match), slug: slug || "demo" };
}

function defaultDemoWorkspace() {
  return {
    id: "demo",
    slug: "demo",
    companyName: "Demo Workspace",
    status: "active",
    loginPath: "/",
    branding: { shortName: "FS", productName: "Field CRM" },
    planLimits: {},
    loginMode: readRootLoginMode(),
  };
}

function readRootLoginMode() {
  if (typeof window === "undefined") return "demo";
  const configured =
    window.__CRM_ROOT_LOGIN_MODE__ ||
    document.querySelector("meta[name='crm-root-login-mode']")?.content ||
    "";
  const normalized = String(configured || "").trim().toLowerCase();
  return ["demo", "password", "ldap"].includes(normalized) ? normalized : "demo";
}

function inferLoginModeFromDirectoryError(error) {
  if (Number(error?.status || 0) !== 403) return "";
  const message = String(error?.message || error?.data?.error || "").toLowerCase();
  if (message.includes("password")) return "password";
  if (message.includes("enterprise")) return "ldap";
  return "";
}

function selectQuickCustomer(customers, users, today = "") {
  const activeOwnerIds = getActiveUserIds(users);
  const activeCustomers = customers.filter((customer) => customerHasActiveOwner(customer, activeOwnerIds));
  const candidates = activeCustomers.length ? activeCustomers : customers;
  if (!candidates.length) return null;
  const dueCustomers = candidates
    .filter((customer) => customer.followUpDate && (!today || customer.followUpDate <= today))
    .sort((a, b) => String(a.followUpDate || "").localeCompare(String(b.followUpDate || "")));
  return dueCustomers[0] || candidates[0];
}

function getQuickCustomerOptions(customers, users) {
  const activeOwnerIds = getActiveUserIds(users);
  return [...customers]
    .filter((customer) => customerHasActiveOwner(customer, activeOwnerIds))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

function getQuickSalesRepOptions(users) {
  return [...users]
    .filter((item) => item.role === "sales" && item.active !== false)
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

function resolveQuickCustomerFocus(value, customerOptions, fallback) {
  if (value.startsWith("employee:")) return null;
  if (!value.startsWith("customer:")) return fallback;
  const customerId = value.slice("customer:".length);
  return customerOptions.find((customer) => customer.id === customerId) || fallback;
}

function resolveQuickRepFocus(value, repOptions, targetRows, fallback) {
  if (!value.startsWith("employee:")) return fallback;
  const userId = value.slice("employee:".length);
  const rep = repOptions.find((item) => item.id === userId);
  if (!rep) return fallback;
  return targetRows.find((row) => row?.userId === rep.id) || {
    userId: rep.id,
    name: rep.name,
    active: rep.active !== false,
    productCategory: "All",
    period: "Monthly",
    revenueTarget: 0,
    quantityTarget: 0,
  };
}

function getActiveUserIds(users) {
  return new Set(users.filter((item) => item.active !== false).map((item) => item.id));
}

function customerHasActiveOwner(customer, activeOwnerIds) {
  return !customer.ownerId || activeOwnerIds.has(customer.ownerId);
}

function selectQuickTargetRep(rows = []) {
  return [...rows]
    .filter((rep) => rep?.userId && rep.active !== false && Number(rep.revenueAchievementPercent || 0) < 80)
    .sort((a, b) => Number(a.revenueAchievementPercent || 0) - Number(b.revenueAchievementPercent || 0))[0] || null;
}

function quickMessageDraft(customer) {
  const dueLabel = customer.followUpDate ? ` due on ${formatDate(customer.followUpDate)}` : "";
  return {
    channel: "WhatsApp",
    template: "Follow-up",
    message: `Following up on ${customer.name}${dueLabel}.`,
    consentRecorded: customer.consentOptIn !== false,
  };
}

function quickTargetDraft(rep) {
  return {
    productCategory: rep.productCategory || "All",
    period: rep.period || "Monthly",
    revenueTarget: Number(rep.revenueTarget || 0),
    quantityTarget: Number(rep.quantityTarget || 0),
  };
}

function readTheme() {
  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return validThemeIds.has(storedTheme) ? storedTheme : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function readView() {
  try {
    const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
    return validViewIds.has(storedView) ? storedView : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

function saveView(view) {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Tab persistence is nice-to-have; navigation still works without storage.
  }
}

function resolveView(view, user) {
  const safeView = validViewIds.has(view) ? view : DEFAULT_VIEW;
  if (safeView === "admin" && user?.role !== "admin") return DEFAULT_VIEW;
  return safeView;
}

function createWorkflowDraftId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveWorkspaceResults(results) {
  const [sessionResult, usersResult, customersResult, activitiesResult, reportResult, resourcesResult] = results;
  const requiredResults = [sessionResult, usersResult, customersResult, activitiesResult, resourcesResult];
  const unauthorized = results.find((result) => result.status === "rejected" && Number(result.reason?.status || 0) === 401);
  if (unauthorized) throw unauthorized.reason;
  const failedRequired = requiredResults.find((result) => result.status === "rejected");
  if (failedRequired) throw failedRequired.reason;
  return [
    asPlainObject(sessionResult.value),
    requireArray(usersResult.value),
    requireArray(customersResult.value),
    requireArray(activitiesResult.value),
    reportResult.status === "fulfilled" ? asOptionalPlainObject(reportResult.value) : null,
    asPlainObject(resourcesResult.value, emptyResources()),
  ];
}

function getBusinessVerticals(customer) {
  if (Array.isArray(customer.businessVerticals) && customer.businessVerticals.length) {
    return customer.businessVerticals.filter(Boolean);
  }
  return [customer.industryType].filter(Boolean);
}

function preserveLastGoodResources(currentResources, nextResources) {
  const failedSections = new Set(nextResources.failedSections || []);
  if (!failedSections.size) return nextResources;
  const merged = { ...nextResources };
  for (const key of failedSections) {
    if (Object.prototype.hasOwnProperty.call(currentResources || {}, key)) {
      merged[key] = currentResources[key];
    }
  }
  return merged;
}

function requireArray(value) {
  if (Array.isArray(value)) return value;
  throw new ApiError("The CRM returned unexpected workspace data. Refresh and try again.", { status: 502, data: {} });
}

function asPlainObject(value, fallback = {}) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : fallback;
}

function asOptionalPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
